import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import api from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(identifier, password);
      // Redirect precedence: ?redirect=... (guest-checkout hint) → location.state.from → default
      const params = new URLSearchParams(location.search);
      const dest =
        params.get("redirect") ||
        location.state?.from ||
        (user?.is_admin ? "/admin" : "/account");
      navigate(dest);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Invalid credentials";
      toast.error(detail, { duration: status === 429 ? 8000 : 4000 });
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid place-items-center px-6 py-16" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center">
          <Logo variant="monogram" className="h-14 w-14 dark:invert" loading="eager" />
        </div>
        <h1 className="mt-10 text-4xl font-serif tracking-tighter text-center">Sign In</h1>
        <p className="mt-3 text-sm opacity-60 text-center">Welcome back to THE FABRICS.</p>

        <button
          type="button"
          onClick={google}
          className="mt-10 w-full h-12 border border-foreground/30 hover:border-foreground flex items-center justify-center gap-3 label-xs"
          data-testid="login-google-btn"
          style={{ transitionProperty: "border-color" }}
        >
          <GoogleG />
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-4">
          <div className="flex-1 border-t" />
          <span className="label-xs opacity-50">OR</span>
          <div className="flex-1 border-t" />
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="label-xs">Email or username</Label>
            <Input
              type="text"
              autoComplete="email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              className="rounded-none mt-2"
              data-testid="login-email"
            />
          </div>
          <div>
            <div className="flex justify-between items-baseline">
              <Label className="label-xs">Password</Label>
              <Link to="/account/forgot" className="label-xs opacity-70 link-underline" data-testid="login-forgot-link">
                Forgot?
              </Link>
            </div>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded-none mt-2"
              data-testid="login-password"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-none label-xs"
            data-testid="login-submit"
          >
            {busy ? "Signing in…" : "Sign In"}
          </Button>
        </form>

        <p className="mt-8 text-sm text-center opacity-80">
          New here?{" "}
          <Link to="/account/signup" className="link-underline" data-testid="login-signup-link">
            Create an account
          </Link>
        </p>
        <p className="mt-4 text-[11px] opacity-50 leading-relaxed text-center">
          Protected — 5 attempts, 15-minute cooldown.
        </p>
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
